import { escapeHtml } from '../helpers.js';

/**
 * Extend MapApp with marker/label management helpers.
 * @param {typeof import('../app.js').default} App
 */
export function applyMarkerMixins(App) {
	/**
	 * Create Leaflet markers for every country label once.
	 */
	App.prototype.addCountryMarkers = function addCountryMarkers() {
		if (
			!Array.isArray(globalThis.countries) ||
			globalThis.countries.length === 0 ||
			this.countryMarkers.length > 0
		) {
			return;
		}

		globalThis.countries.forEach((country) => {
			const marker = globalThis.L.marker([country.lat, country.lon], {
				icon: this.createCountryIcon(country.name),
				pane: this.countryMarkerPane
			});
			marker.addTo(this.countryLayer);

			const entry = {
				marker,
				data: country,
				labelEl: null,
				labelElements: [],
				lastFontSize: null,
				isHighlighted: false
			};
			entry.slug = this.getInfoId(country);

			if (this.countryEntryMap) {
				this.countryEntryMap.set(country, entry);
			}

			marker.on('click', (event) => {
				this.handleEntitySelection('nation', country, event);
			});

			marker.on('add', () => {
				const element = marker.getElement();
				if (element) {
					entry.labelEl = element.querySelector('.label_country');
					if (entry.labelEl) {
						entry.labelElements.push(entry.labelEl);
					}
				}
				if (entry.isHighlighted && entry.labelEl) {
					entry.labelEl.classList.add('is-highlighted');
					element.classList.add('is-highlighted-marker');
				}
			});

			this.setCountryMarkerDraggable(entry, this.isEditMode());
			marker.on('dragend', (event) => {
				this.handleCountryDragEnd(entry, event);
			});

			this.countryMarkers.push(entry);
		});
	};

	/**
	 * Wire tooltip click/add events and track DOM references.
	 * @param {object} entry
	 * @param {L.Tooltip} tooltip
	 * @param {object|null} cloneRef
	 */
	App.prototype.attachCityTooltipEvents = function attachCityTooltipEvents(entry, tooltip, cloneRef) {
		if (!entry || !tooltip) {
			return;
		}
		const city = entry.data;
		tooltip.on('click', (event) => {
			if (event && event.originalEvent) {
				globalThis.L.DomEvent.stopPropagation(event.originalEvent);
			}
			this.handleEntitySelection('city', city, event);
		});
		tooltip.on('add', () => {
			const tooltipElement = tooltip.getElement();
			if (!tooltipElement) {
				return;
			}
			if (cloneRef) {
				cloneRef.tooltipEl = tooltipElement;
			} else {
				entry.tooltipEl = tooltipElement;
			}
			this.hideCityTooltipElement(tooltipElement);
			if (entry.isHighlighted) {
				tooltipElement.classList.add('is-highlighted');
			}
		});
	};

	/**
	 * Run a callback against the DOM element for a country marker.
	 * @param {object} entry
	 * @param {Function} callback
	 */
	App.prototype.forEachCountryMarkerElement = function forEachCountryMarkerElement(entry, callback) {
		if (!entry || typeof callback !== 'function') {
			return;
		}
		const processMarker = (marker) => {
			if (!marker || typeof marker.getElement !== 'function') {
				return;
			}
			const element = marker.getElement();
			if (element) {
				callback(element);
			}
		};
		processMarker(entry.marker);
	};

	/**
	 * Collect reusable DOM references for a country's label nodes.
	 * @param {object} entry
	 * @returns {HTMLElement[]}
	 */
	App.prototype.getCountryLabelElements = function getCountryLabelElements(entry) {
		if (!entry) {
			return [];
		}
		if (!Array.isArray(entry.labelElements)) {
			entry.labelElements = [];
		}
		if (!entry.labelEl && entry.marker && typeof entry.marker.getElement === 'function') {
			const baseElement = entry.marker.getElement();
			if (baseElement) {
				const baseLabel = baseElement.querySelector('.label_country');
				if (baseLabel) {
					entry.labelEl = baseLabel;
					entry.labelElements.push(baseLabel);
				}
			}
		}
		return entry.labelElements.filter((label) => !!label);
	};

	/**
	 * Run a callback for the DOM elements backing a city marker.
	 * @param {object} entry
	 * @param {Function} callback
	 */
	App.prototype.forEachCityMarkerElement = function forEachCityMarkerElement(entry, callback) {
		if (!entry || typeof callback !== 'function') {
			return;
		}
		const processMarker = (marker) => {
			if (!marker || typeof marker.getElement !== 'function') {
				return;
			}
			const el = marker.getElement();
			if (el) {
				callback(el);
			}
		};
		processMarker(entry.marker);
	};

	/**
	 * Invoke a callback with tooltip elements for a city entry.
	 * @param {object} entry
	 * @param {Function} callback
	 */
	App.prototype.forEachCityTooltipElement = function forEachCityTooltipElement(entry, callback) {
		if (!entry || typeof callback !== 'function') {
			return;
		}
		const processTooltip = (tooltip, getElement, setElement) => {
			if (!tooltip || typeof tooltip.getElement !== 'function') {
				return;
			}
			let element = getElement();
			if (!element) {
				element = tooltip.getElement();
			}
			if (element) {
				setElement(element);
				callback(element);
			}
		};
		if (entry.tooltip) {
			processTooltip(
				entry.tooltip,
				() => entry.tooltipEl,
				(element) => {
					entry.tooltipEl = element;
				}
			);
		}
	};

	/**
	 * Incrementally instantiate city markers to avoid blocking the UI.
	 */
	App.prototype.scheduleCityMarkers = function scheduleCityMarkers() {
		if (
			!Array.isArray(globalThis.cities) ||
			globalThis.cities.length === 0 ||
			this.cityMarkers.length >= globalThis.cities.length ||
			this.cityMarkerLoader !== null
		) {
			return;
		}

		const cityQueue = globalThis.cities.filter((entry) => entry && !entry.synthetic);
		let index = 0;
		const total = cityQueue.length;
		const chunkSize = 24;
		const useIdle = typeof window.requestIdleCallback === 'function';

		const process = (deadline) => {
			let processed = 0;
			const hasDeadline =
				deadline && typeof deadline.timeRemaining === 'function';

			while (index < total && processed < chunkSize) {
				if (hasDeadline && deadline.timeRemaining() <= 1) {
					break;
				}

				const city = cityQueue[index];
				index += 1;

				if (this.getCityEntry(city)) {
					continue;
				}

				this.ensureCityEntry(city);
				processed += 1;
			}

			if (index < total) {
				scheduleNext();
				return;
			}

		this.cityMarkerLoader = null;
		this.schedulePanelReveal(360);
		};

		const scheduleNext = () => {
			if (useIdle) {
				this.cityMarkerLoader = window.requestIdleCallback(process, { timeout: 500 });
				return;
			}
			this.cityMarkerLoader = window.requestAnimationFrame(() => {
				process({
					timeRemaining() {
						return Number.POSITIVE_INFINITY;
					}
				});
			});
		};

		scheduleNext();
	};

	/**
	 * Retrieve the marker bookkeeping entry for a city data object.
	 * @param {object} city
	 * @returns {object|null}
	 */
	App.prototype.getCityEntry = function getCityEntry(city) {
		if (!city) {
			return null;
		}
		if (this.cityEntryMap) {
			const entry = this.cityEntryMap.get(city);
			if (entry) {
				return entry;
			}
		} else {
			for (let i = 0; i < this.cityMarkers.length; i += 1) {
				if (this.cityMarkers[i] && this.cityMarkers[i].data === city) {
					return this.cityMarkers[i];
				}
			}
		}
		return null;
	};

	/**
	 * Guarantee that a city entry exists, creating it if necessary.
	 * @param {object} city
	 * @returns {object|null}
	 */
	App.prototype.ensureCityEntry = function ensureCityEntry(city) {
		let entry = this.getCityEntry(city);
		if (entry) {
			return entry;
		}
		entry = this.createCityEntry(city);
		if (entry && this.cityEntryMap) {
			this.cityEntryMap.set(city, entry);
		}
		return entry;
	};

	/**
	 * Build a marker + tooltip entry for a single city.
	 * @param {object} city
	 * @returns {object|null}
	 */
	App.prototype.createCityEntry = function createCityEntry(city) {
		if (!city || typeof city.lat !== 'number' || typeof city.lon !== 'number') {
			return null;
		}
		const icon = this.getCityMarkerIcon();
		const alignPreference = 'right';
		const marker = globalThis.L.marker([city.lat, city.lon], {
			icon,
			bubblingMouseEvents: false
		});
		marker.addTo(this.cityLayer);

		marker.on('click', (event) => {
			this.handleEntitySelection('city', city, event);
		});

		const tooltipOptions = {
			permanent: true,
			className: 'label_city tooltip-city',
			interactive: true,
			direction: 'right',
			offset: globalThis.L.point(12, 8)
		};
		const tooltip = marker
			.bindTooltip(escapeHtml(city.name), tooltipOptions)
			.getTooltip();

		const entry = {
			marker,
			tooltip,
			alignPreference,
			data: city,
			tooltipEl: null,
			lastFontSize: null,
			isHighlighted: false,
			slug: this.getInfoId(city),
		};

		this.attachCityTooltipEvents(entry, tooltip, null);

		marker.on('add', () => {
			const iconEl = marker.getElement();
			if (entry.isHighlighted && iconEl) {
				iconEl.classList.add('is-highlighted-marker');
			}
		});

		this.cityMarkers.push(entry);
		if (city && city.name) {
			this.updateCityCoordinateIndex([city.name, city.slug], city.lat, city.lon);
		}
		this.setCityMarkerDraggable(entry, this.isEditMode());
		marker.on('dragend', (event) => {
			this.handleCityDragEnd(entry, event);
		});
		this.hideCityTooltip(entry);
		return entry;
	};

	/**
	 * Lazily create the default city marker icon.
	 * @returns {L.Icon}
	 */
	App.prototype.getCityMarkerIcon = function getCityMarkerIcon() {
		if (this.cityMarkerIcon) {
			return this.cityMarkerIcon;
		}
		this.cityMarkerIcon = globalThis.L.icon({
			iconUrl: 'images/city-marker.svg',
			iconSize: [14, 14],
			iconAnchor: [7, 7],
			className: 'city-dot-icon'
		});
		return this.cityMarkerIcon;
	};

	/**
	 * Lazily create the compact/star-mode city marker icon.
	 * @returns {L.DivIcon}
	 */
	App.prototype.getCityCompactMarkerIcon = function getCityCompactMarkerIcon() {
		if (this.cityCompactMarkerIcon) {
			return this.cityCompactMarkerIcon;
		}
		this.cityCompactMarkerIcon = globalThis.L.divIcon({
			className: 'synthetic-dot-icon city-compact-dot-icon',
			html: '<span class="synthetic-dot-icon__inner"></span>',
			iconSize: [8, 8],
			iconAnchor: [4, 4]
		});
		return this.cityCompactMarkerIcon;
	};

	/**
	 * Hide a city's tooltip across all tracked DOM variants.
	 * @param {object} entry
	 */
	App.prototype.hideCityTooltip = function hideCityTooltip(entry) {
		if (!entry) {
			return;
		}
		this.forEachCityTooltipElement(entry, (element) => {
			this.hideCityTooltipElement(element);
		});
	};

	/**
	 * Apply inline styles to fully hide a tooltip element.
	 * @param {HTMLElement} element
	 */
	App.prototype.hideCityTooltipElement = function hideCityTooltipElement(element) {
		if (!element) {
			return;
		}
		element.style.display = 'none';
		element.style.pointerEvents = 'none';
		element.style.opacity = '0';
	};

	/**
	 * Respond to map zoom updates by scaling labels and toggling pins.
	 */
	App.prototype.onZoomChange = function onZoomChange() {
		if (!this.map) {
			return;
		}

		const zoom = this.map.getZoom();
		const sizes = this.getLabelSizes(zoom);
		const compactCityPins = zoom < (this.defaultZoomLevel || this.cityZoomThreshold || 3);

		this.applyCityMarkerCompactMode(compactCityPins);

		const starModeActive =
			typeof this.isStarMode === 'function' && this.isStarMode();
		const showCityLabels = !starModeActive && zoom >= this.cityZoomThreshold;

		for (let i = 0; i < this.countryMarkers.length; i += 1) {
			const entry = this.countryMarkers[i];
			const labelElements = this.getCountryLabelElements(entry);
			if (!labelElements.length) {
				continue;
			}
			if (entry.lastFontSize !== sizes.country) {
				for (let j = 0; j < labelElements.length; j += 1) {
					const label = labelElements[j];
					label.style.fontSize = `${sizes.country}px`;
					label.style.height = `${sizes.country + 10}px`;
				}
				entry.lastFontSize = sizes.country;
			}
			for (let j = 0; j < labelElements.length; j += 1) {
				const label = labelElements[j];
				label.style.opacity = '0.75';
				label.classList.toggle('is-highlighted', entry.isHighlighted);
			}
			this.forEachCountryMarkerElement(entry, (markerEl) => {
				markerEl.classList.toggle('is-highlighted-marker', entry.isHighlighted);
			});
		}

		if (starModeActive) {
			if (typeof this.hideCityLabelsForStarMode === 'function') {
				this.hideCityLabelsForStarMode();
			}
			return;
		}

		if (showCityLabels) {
			for (let j = 0; j < this.cityMarkers.length; j += 1) {
				const cityEntry = this.cityMarkers[j];
				const tooltip = cityEntry.tooltip;
				if (!tooltip) {
					continue;
				}
				const tooltipElement = cityEntry.tooltipEl || tooltip.getElement();
				if (!tooltipElement) {
					continue;
				}
				cityEntry.tooltipEl = tooltipElement;
				this.forEachCityTooltipElement(cityEntry, (element) => {
					if (cityEntry.lastFontSize !== sizes.city) {
						element.style.fontSize = `${sizes.city}px`;
					}
					element.style.display = '';
					element.style.opacity = '0.75';
					element.style.pointerEvents = 'auto';
					element.classList.toggle('is-highlighted', cityEntry.isHighlighted);
				});
				this.forEachCityMarkerElement(cityEntry, (markerEl) => {
					markerEl.classList.toggle('is-highlighted-marker', cityEntry.isHighlighted);
				});
				cityEntry.lastFontSize = sizes.city;
			}
		} else {
			for (let k = 0; k < this.cityMarkers.length; k += 1) {
				const entry = this.cityMarkers[k];
				this.hideCityTooltip(entry);
				this.forEachCityMarkerElement(entry, (markerEl) => {
					markerEl.classList.remove('is-highlighted-marker');
				});
				entry.lastFontSize = null;
			}
		}
		if (typeof this.updateCompactStarDots === 'function') {
			this.updateCompactStarDots();
		}
	};

	/**
	 * Toggle the compact icon style for every city marker.
	 * @param {boolean} enable
	 */
	App.prototype.applyCityMarkerCompactMode = function applyCityMarkerCompactMode(enable) {
		if (!Array.isArray(this.cityMarkers)) {
			return;
		}
		if (this.cityMarkersCompactMode === enable) {
			return;
		}
		const icon = enable ? this.getCityCompactMarkerIcon() : this.getCityMarkerIcon();
		for (let i = 0; i < this.cityMarkers.length; i += 1) {
			const entry = this.cityMarkers[i];
			if (!entry || !entry.marker || typeof entry.marker.setIcon !== 'function') {
				continue;
			}
			entry.marker.setIcon(icon);
		}
		this.cityMarkersCompactMode = enable;
	};
	/**
	 * Map discrete zoom levels to font sizes for markers.
	 * @param {number} zoom
	 * @returns {{country:number,city:number}}
	 */
	App.prototype.getLabelSizes = function getLabelSizes(zoom) {
		switch (zoom) {
			case 0:
				return { country: 8, city: 0 };
			case 1:
				return { country: 12, city: 0 };
			case 2:
				return { country: 18, city: 8 };
			case 3:
				return { country: 24, city: 12 };
			case 4:
				return { country: 36, city: 18 };
			case 5:
				return { country: 48, city: 24 };
			case 6:
				return { country: 54, city: 32 };
			default:
			 return { country: 18, city: 12 };
		}
	};

	/**
	 * Produce the HTML-backed label icon for a country marker.
	 * @param {string} name
	 * @returns {L.DivIcon}
	 */
	App.prototype.createCountryIcon = function createCountryIcon(name) {
		const content =
			'<div class="label-anchor"><div class="label_country">' +
			escapeHtml(name) +
			'</div></div>';

		return globalThis.L.divIcon({
			className: 'country-label-icon',
			html: content,
			iconSize: null
		});
	};
	/**
	 * Apply highlight styling to a city/country (or synthetic city).
	 * @param {'city'|'nation'} kind
	 * @param {object} entity
	 */
	App.prototype.highlightEntity = function highlightEntity(kind, entity) {
		if (!entity) {
			return;
		}
		if (kind === 'city' && entity.synthetic) {
			const syntheticEntry = this.getSyntheticCityEntryRef(entity);
			if (!syntheticEntry) {
				this.clearHighlight();
				return;
			}
			if (this.highlightedState && this.highlightedState.entry === syntheticEntry) {
				return;
			}
			this.clearHighlight();
			this.applySyntheticHighlight(syntheticEntry, true);
			this.highlightedState = { entry: syntheticEntry, kind: 'synthetic-city' };
			this.clearBoundaryOverlay();
			this.updateSearchSelection();
			return;
		}
		const mapRef = kind === 'city' ? this.cityEntryMap : this.countryEntryMap;
		if (!mapRef) {
			return;
		}
		if (kind === 'city') {
			this.ensureCityEntry(entity);
		}
		const entry = mapRef.get(entity);
		if (!entry) {
			this.clearHighlight();
			return;
		}
		if (this.highlightedState && this.highlightedState.entry === entry) {
			return;
		}
		this.clearHighlight();
		this.applyHighlight(entry, kind, true);
		this.highlightedState = { entry, kind };
		if (kind === 'nation') {
			this.activateBoundaryOverlay(entity);
		} else {
			this.clearBoundaryOverlay();
		}
		this.updateSearchSelection();
	};

	/**
	 * Remove highlight state and boundary overlays, if any.
	 */
	App.prototype.clearHighlight = function clearHighlight() {
		if (!this.highlightedState) {
			this.clearBoundaryOverlay();
			return;
		}
		if (this.highlightedState.kind === 'synthetic-city') {
			this.applySyntheticHighlight(this.highlightedState.entry, false);
			this.highlightedState = null;
			this.clearBoundaryOverlay();
			this.updateSearchSelection();
			return;
		}
		this.applyHighlight(this.highlightedState.entry, this.highlightedState.kind, false);
		this.highlightedState = null;
		this.clearBoundaryOverlay();
		this.updateSearchSelection();
	};

	/**
	 * Find an in-memory synthetic entry for the provided city object.
	 * @param {object} city
	 * @returns {object|null}
	 */
	App.prototype.getSyntheticCityEntryRef = function getSyntheticCityEntryRef(city) {
		if (!city || !Array.isArray(this.syntheticCityEntries)) {
			return null;
		}
		for (let i = 0; i < this.syntheticCityEntries.length; i += 1) {
			const entry = this.syntheticCityEntries[i];
			if (entry && entry.city === city) {
				return entry;
			}
		}
		return null;
	};

	/**
	 * Toggle synthetic marker highlight styles.
	 * @param {object} entry
	 * @param {boolean} enable
	 */
	App.prototype.applySyntheticHighlight = function applySyntheticHighlight(entry, enable) {
		if (!entry || !entry.marker) {
			return;
		}
		const el = entry.marker.getElement();
		if (el) {
			el.classList.toggle('is-highlighted-marker', !!enable);
		}
		entry.isHighlighted = !!enable;
	};

	/**
	 * Toggle highlight classes for a city or nation entry.
	 * @param {object} entry
	 * @param {'city'|'nation'} kind
	 * @param {boolean} enable
	 */
	App.prototype.applyHighlight = function applyHighlight(entry, kind, enable) {
		if (!entry) {
			return;
		}
		entry.isHighlighted = enable;

		if (kind === 'city') {
			this.forEachCityMarkerElement(entry, (markerEl) => {
				markerEl.classList.toggle('is-highlighted-marker', enable);
			});
			this.forEachCityTooltipElement(entry, (tooltipEl) => {
				tooltipEl.classList.toggle('is-highlighted', enable);
			});
		} else {
			this.forEachCountryMarkerElement(entry, (markerEl) => {
				markerEl.classList.toggle('is-highlighted-marker', enable);
			});
			const labelElements = this.getCountryLabelElements(entry);
			for (let i = 0; i < labelElements.length; i += 1) {
				labelElements[i].classList.toggle('is-highlighted', enable);
			}
		}
	};
}
